import React, { useState, useEffect } from 'react';
import { ThunkDispatch } from "@reduxjs/toolkit";
import { connect, useDispatch } from "react-redux";
import { Tooltip } from 'primereact/tooltip';
import { imageEditingActions, ImageEditingState, Template } from '../../reducers/imageEditing';
import { toastActions } from '../../reducers/toast';
import { PlanRestrictions } from '../../utils/planRestrictions';
import { InfoIcon } from '../SVGIcons';
import MyPageModal from '../mypage/MyPageModal';
import TemplateSaveModal from './TemplateSaveModal';
import TemplateOverwriteModal from './TemplateOverwriteModal';
import { HintPopup } from '../common/HintPopup';

// State型定義
interface RootState {
  imageEditing: ImageEditingState;
  currentUser: {
    planType?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  return {
    processingMethod: state.imageEditing.processingMethod,
    selectedSettings: state.imageEditing.selectedSettings,
    features: state.imageEditing.features,
    uploadedImages: state.imageEditing.uploadedImages,
    templates: state.imageEditing.templates,
    selectedTemplate: state.imageEditing.selectedTemplate,
    currentUser: state.currentUser,
  };
}

interface ImageEditingSettingsPanelProps {
  processingMethod: 'single' | 'individual';
  selectedSettings: string;
  features: ImageEditingState['features'];
  uploadedImages: ImageEditingState['uploadedImages'];
  templates: Template[];
  selectedTemplate: Template | null;
  currentUser: {
    planType?: string;
    [key: string]: any;
  };
}


interface ProcessingFeature {
  id: string;
  label: string;
  hasInfo?: boolean;
  hasSettings?: boolean;
  planRestricted?: boolean;
  comingSoon?: boolean;
}

const processingFeatures: ProcessingFeature[] = [
  { id: 'watermark', label: 'ウォーターマーク', hasInfo: true },
  { id: 'compression', label: '画像圧縮', hasInfo: true, hasSettings: true },
  { id: 'contrastAdjustment', label: 'コントラスト', hasInfo: true, hasSettings: true },
  { id: 'brightnessAdjustment', label: '明るさ', hasInfo: true, hasSettings: true },
  { id: 'whiteBalance', label: 'ホワイトバランス', hasInfo: true, hasSettings: true },
  { id: 'mosaic', label: 'モザイク加工', hasInfo: true, planRestricted: true },
  { id: 'highResolution', label: '高解像度処理', hasInfo: true, planRestricted: true },
  { id: 'levelAdjustment', label: '水平調整', hasInfo: true, planRestricted: true, comingSoon: true },
  { id: 'backgroundProcessing', label: '青空加工', hasInfo: true, planRestricted: true }
];

// 処理モードに応じたツールチップを生成する関数
const getTooltips = (processingMethod: 'single' | 'individual'): { [key: string]: string } => {
  const compressionText = processingMethod === 'individual'
    ? '・高機能スマホやプロの撮影で画像データが大きく、ポータルに利用できない場合にご利用頂けます。\n・画像サイズを5MB以下、もしくは1MB以下に圧縮する機能。\n・入力画像がすでに該当サイズ以下の場合は処理が行われません。\n・画像を圧縮したい方はこちらをONへ変更し、ダウンロードボタンを押してください。'
    : '・高機能スマホやプロの撮影で画像データが大きく、ポータルに利用できない場合にご利用頂けます。\n・画像サイズを5MB以下、もしくは1MB以下に圧縮する機能。\n・入力画像がすでに該当サイズ以下の場合は処理が行われません。\n・画像を圧縮したい方はこちらをONへ変更し、実行してください。';

  return {
    processingMethod: '・一括処理：最大50枚を一度に処理。出力結果確認後の修正は不可。\n・個別処理：最大50枚を1枚づつ処理。出力結果確認後の修正は可能。',
    template: '・予め設定した加工機能のON/OFFの設定をテンプレートとして保存＆利用が可能。',
    watermark: '・「アカウント設定」で設定したロゴを画像上に表示する機能。\n・設定を変更する場合は、「設定はこちら」をクリック。',
    compression: compressionText,
    contrastAdjustment: '数値が高いと被写体がはっきりと際立ち、数値が低いと全体が柔らかく、落ち着いた印象を与えます。',
    brightnessAdjustment: '画像の明るさを調整します。数値が高いと明るく、低いと暗くなります。',
    whiteBalance: '・太陽光：見た目に近い自然な色\n・蛍光灯：暖かさが表現された色\n・電球色：青みがかった色',
    mosaic: '「人の顔」や「ナンバープレート」をAIが自動で検出し、モザイク処理を行います。詳細はヒントアイコンをクリック。',
    highResolution: '画像の解像度を向上させ、より鮮明な画質に仕上げます。',
    levelAdjustment: '画像の水平を自動で調整し、傾きを補正します。',
    backgroundProcessing: '写真の空を自動で検出し、美しい青空に変換します。詳細はヒントアイコンをクリック。'
  };
};

const ImageEditingSettingsPanel: React.FC<ImageEditingSettingsPanelProps> = (props) => {
  const { processingMethod, selectedSettings, features, uploadedImages, templates, selectedTemplate, currentUser } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [showMyPageModal, setShowMyPageModal] = useState(false);
  const [showTemplateSaveModal, setShowTemplateSaveModal] = useState(false);
  const [showTemplateOverwriteModal, setShowTemplateOverwriteModal] = useState(false);
  const [contrastInputValue, setContrastInputValue] = useState<string>('');
  const [isContrastInputFocused, setIsContrastInputFocused] = useState(false);
  const [brightnessInputValue, setBrightnessInputValue] = useState<string>('');
  const [isBrightnessInputFocused, setIsBrightnessInputFocused] = useState(false);
  const [showClearHint, setShowClearHint] = useState(false);
  const [dontShowClearAgain, setDontShowClearAgain] = useState(false);

  // プラン制御
  const hasAdvancedFeatures = PlanRestrictions.hasAdvancedImageEditing(currentUser?.planType);
  const canUseIndividualProcessing = !PlanRestrictions.shouldDisableIndividualProcessing(
    currentUser?.planType,
    uploadedImages.length
  );
  const planRestrictionMessage = PlanRestrictions.getPlanRestrictionMessage(currentUser?.planType, processingMethod);

  // 処理モードに応じたツールチップを取得
  const tooltips = getTooltips(processingMethod);


  // プラン制限の更新
  useEffect(() => {
    if (currentUser?.planType) {
      dispatch(imageEditingActions.updatePlanRestrictions({ planType: currentUser.planType }));
    }
  }, [currentUser?.planType, dispatch]);

  // テンプレート取得
  useEffect(() => {
    dispatch(imageEditingActions.fetchTemplates());
  }, [dispatch]);

  // Tooltipの再初期化（processingMethodが変更された時）
  useEffect(() => {
    // PrimeReactのTooltipを再初期化するためのタイマー
    const timer = setTimeout(() => {
      // Tooltipの再スキャンをトリガー
      const tooltipElements = document.querySelectorAll('[data-pr-tooltip]');
      tooltipElements.forEach((element) => {
        // 要素を強制的に再レンダリング
        const tooltip = element.getAttribute('data-pr-tooltip');
        if (tooltip) {
          element.setAttribute('data-pr-tooltip', tooltip);
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [processingMethod, hasAdvancedFeatures]);

  // クリアヒントのlocalStorageチェック
  useEffect(() => {
    const hidden = localStorage.getItem('settingsClearHintHidden');
    if (hidden === 'true' && showClearHint) {
      setShowClearHint(false);
    }
  }, [showClearHint]);

  const handleProcessingMethodChange = (method: 'single' | 'individual') => {
    // 個別処理への切り替え時のチェック
    if (method === 'individual') {
      // Mini/Liteプランの場合
      if (!hasAdvancedFeatures) {
        dispatch(toastActions.toastError(
          'Basic/Pro/Eliteプランへのアップグレードが必要です'
        ));
        return;
      }
    }
    dispatch(imageEditingActions.setProcessingMethod(method));
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;

    if (templateId === '') {
      // テンプレート未選択
      dispatch(imageEditingActions.selectTemplate(null));
      return;
    }

    // ユーザーテンプレートを検索して適用
    // IDは文字列または数値の可能性があるので、両方で比較
    const userTemplate = templates.find(t =>
      String(t.id) === templateId || t.id === templateId
    );
    if (userTemplate) {
      dispatch(imageEditingActions.selectTemplate(userTemplate));
      dispatch(imageEditingActions.applyTemplate(userTemplate));
    }
  };

  const handleTemplateSave = () => {
    // 現在有効な機能があるかチェック
    const hasEnabledFeatures = Object.values(features).some(f => f.enabled);
    if (!hasEnabledFeatures) {
      dispatch(toastActions.toastError('保存する加工機能を選択してください'));
      return;
    }

    // テンプレート数をチェック
    if (templates.length < 5) {
      setShowTemplateSaveModal(true);
    } else {
      setShowTemplateOverwriteModal(true);
    }
  };

  const handleFeatureToggle = (featureId: string) => {
    dispatch(imageEditingActions.toggleFeature(featureId));

    // 圧縮をONにした場合、デフォルトで5MBを設定
    if (featureId === 'compression' && !features.compression?.enabled) {
      dispatch(imageEditingActions.updateFeatureSettings({
        featureName: 'compression',
        settings: { targetSize: '5MB' }
      }));
    }
  };

  // コントラスト調整のハンドラ
  const handleContrastChange = (value: number) => {
    dispatch(imageEditingActions.updateContrastSettings({
      brightness: features.contrastAdjustment?.settings?.brightness || 0,  // 既存の明るさを保持
      contrast: value,
      saturation: features.contrastAdjustment?.settings?.saturation || 0  // 既存の彩度を保持
    }));
  };

  // 明るさ調整のハンドラ
  const handleBrightnessChange = (value: number) => {
    dispatch(imageEditingActions.updateFeatureSettings({
      featureName: 'brightnessAdjustment',
      settings: { brightness: value }
    }));
  };

  // ホワイトバランスプリセットのハンドラ
  const handleWhiteBalancePreset = (preset: 'sunlight' | 'fluorescent' | 'incandescent') => {
    let temperature = 0;
    let tint = 0;

    switch (preset) {
      case 'sunlight':
        // 太陽光: 4500K-5000K（中性、自然光）
        temperature = 4750;
        break;
      case 'fluorescent':
        // 蛍光灯: 5750K（高い色温度 = 寒色系・青白い光）
        temperature = 5750;
        break;
      case 'incandescent':
        // 電球色: 3750K（低い色温度 = 暖色系・オレンジ色の光）
        temperature = 3750;
        break;
    }

    dispatch(imageEditingActions.updateWhiteBalanceSettings({
      temperature,
      tint
    }));
  };

  // クリア機能のハンドラ
  const handleCloseClearHint = () => {
    if (dontShowClearAgain) {
      localStorage.setItem('settingsClearHintHidden', 'true');
    }
    setShowClearHint(false);
    setDontShowClearAgain(false);
  };

  const handleExecuteClear = () => {
    handleCloseClearHint();
    dispatch(imageEditingActions.clearAllSettings());
    dispatch(toastActions.toastSuccess('設定をクリアしました'));
  };

  const handleClearSettings = () => {
    const hidden = localStorage.getItem('settingsClearHintHidden');
    if (hidden === 'true') {
      // 直接クリア実行
      dispatch(imageEditingActions.clearAllSettings());
      dispatch(toastActions.toastSuccess('設定をクリアしました'));
    } else {
      // ヒントポップアップを表示
      setShowClearHint(true);
    }
  };

  // 有効な設定があるかチェック
  const hasAnyEnabledSettings =
    features.watermark.enabled ||
    features.compression.enabled ||
    features.contrastAdjustment?.enabled ||
    features.brightnessAdjustment?.enabled ||
    features.whiteBalance?.enabled ||
    selectedTemplate !== null;

  return (
    <div className="h-full overflow-visible relative">
      <style>{`
        .orange-tooltip .p-tooltip-text {
          background-color: rgb(249 115 22);
          color: white;
        }
        .orange-tooltip .p-tooltip-arrow {
          border-right-color: rgb(249 115 22);
        }
      `}</style>
      <Tooltip target=".tooltip-processing-method" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-template" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-watermark" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-compression" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-contrastAdjustment" className="orange-tooltip max-w-xs" key={`contrast-${processingMethod}-${hasAdvancedFeatures}`} />
      <Tooltip target=".tooltip-brightnessAdjustment" className="orange-tooltip max-w-xs" key={`brightness-${processingMethod}-${hasAdvancedFeatures}`} />
      <Tooltip target=".tooltip-whiteBalance" className="orange-tooltip max-w-xs" key={`wb-${processingMethod}-${hasAdvancedFeatures}`} />
      {/* モザイク加工〜背景処理のツールチップは一時的に非表示
      <Tooltip target=".tooltip-mosaic" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-highResolution" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-levelAdjustment" className="orange-tooltip max-w-xs" />
      <Tooltip target=".tooltip-backgroundProcessing" className="orange-tooltip max-w-xs" />
      */}
      <div className="h-full overflow-y-auto overflow-x-visible">
        {/* 設定セクション */}
        <div className="rounded-lg mb-4 relative border-2 border-gray-300 bg-white overflow-hidden">
        <div className="text-gray-800 text-center py-2 px-4 font-semibold bg-gray-100">
          設定
        </div>
        <div className="p-4">
          {/* 処理方法 */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">処理方法</span>
              <InfoIcon
                className="tooltip-processing-method w-4 h-4 cursor-pointer text-gray-400"
                data-pr-tooltip={tooltips.processingMethod}
                data-pr-position="right"
              />
            </div>
            <div className="flex">
              <button
                type="button"
                onClick={() => handleProcessingMethodChange('individual')}
                disabled={!canUseIndividualProcessing}
                className={`px-4 py-1.5 text-sm font-medium transition-colors rounded-l-md ${
                  processingMethod === 'individual'
                    ? 'bg-orange-500 text-white'
                    : canUseIndividualProcessing
                    ? 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                }`}
              >
                個別処理
              </button>
              <button
                type="button"
                onClick={() => handleProcessingMethodChange('single')}
                className={`px-4 py-1.5 text-sm font-medium transition-colors rounded-r-md ${
                  processingMethod === 'single'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                一括処理
              </button>
            </div>
          </div>


          {/* テンプレート */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">テンプレート</span>
              <InfoIcon
                className="tooltip-template w-4 h-4 cursor-pointer text-gray-400"
                data-pr-tooltip={tooltips.template}
                data-pr-position="right"
              />
            </div>
            <div className="relative" style={{ width: '180px' }}>
              <select
                value={selectedTemplate ? String(selectedTemplate.id) : ''}
                onChange={handleTemplateChange}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md appearance-none pr-10 truncate ${
                  hasAdvancedFeatures
                    ? 'bg-white text-gray-700 cursor-pointer hover:border-gray-400'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!hasAdvancedFeatures}
                title={selectedTemplate ? selectedTemplate.name : 'テンプレートを選択'}
              >
                <option value="">テンプレートを選択</option>

                {/* 保存済みテンプレート */}
                {templates.map((template) => (
                  <option key={String(template.id)} value={String(template.id)} title={template.name}>
                    {template.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 加工機能セクション */}
      <div className="rounded-lg relative overflow-hidden border-2 border-gray-300 bg-white">
        <div className="text-gray-800 text-center py-2 px-4 font-semibold bg-gray-100">
          加工機能
        </div>
        <div className="p-3 relative">
          <div className="space-y-2">
            {/* ウォーターマーク、圧縮、コントラスト、明るさ、ホワイトバランス */}
            {processingFeatures.slice(0, 5).filter((feature) => {
              // コントラスト調整、明るさ調整、ホワイトバランスは個別処理モードのみ表示（全プラン対応）
              if (feature.id === 'contrastAdjustment' || feature.id === 'brightnessAdjustment' || feature.id === 'whiteBalance') {
                return processingMethod === 'individual';
              }
              return true;
            }).map((feature) => {
              const featureState = features[feature.id as keyof typeof features];
              const isEnabled = featureState?.enabled || false;
              const isPlanRestricted = feature.planRestricted || false;

              return (
                <div key={feature.id}>
                  <div
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      isPlanRestricted ? 'bg-gray-300' : ''
                    }`}
                  >
                    <div className="flex-1">
                      <span className={`text-sm ${isPlanRestricted ? 'text-gray-600' : 'text-gray-700'}`}>
                        {feature.label}
                      </span>
                      {feature.id === 'watermark' && (
                        <div className="text-xs text-gray-500 ml-2">
                          設定は<a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowMyPageModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >こちら</a>
                        </div>
                      )}
                    </div>
                  <div className="flex items-center gap-2">
                    {feature.hasInfo && tooltips[feature.id] && (
                      <InfoIcon
                        className={`tooltip-${feature.id} w-4 h-4 cursor-pointer text-gray-400`}
                        data-pr-tooltip={tooltips[feature.id]}
                        data-pr-position="left"
                      />
                    )}
                    <div className="flex">
                      <button
                        type="button"
                        onClick={() => !isPlanRestricted && !isEnabled && handleFeatureToggle(feature.id)}
                        disabled={isPlanRestricted || isEnabled}
                        className={`px-4 py-1.5 text-sm font-medium rounded-l-md transition-colors ${
                          isEnabled && !isPlanRestricted
                            ? 'bg-orange-500 text-white'
                            : isPlanRestricted
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                        }`}
                      >
                        ON
                      </button>
                      <button
                        type="button"
                        onClick={() => !isPlanRestricted && isEnabled && handleFeatureToggle(feature.id)}
                        disabled={isPlanRestricted || !isEnabled}
                        className={`px-4 py-1.5 text-sm font-medium rounded-r-md transition-colors ${
                          !isEnabled || isPlanRestricted
                            ? 'bg-gray-500 text-white'
                            : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                        }`}
                      >
                        OFF
                      </button>
                    </div>
                  </div>
                  </div>
                  {/* 圧縮設定 */}
                  {feature.id === 'compression' && features.compression?.enabled && (
                    <div className="-mt-2 p-1 bg-gray-50 rounded" style={{ marginLeft: '20px', marginRight: '12px' }}>
                <div className="flex gap-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="compressionSize"
                      value="5MB"
                      checked={features.compression.settings?.targetSize === '5MB' || !features.compression.settings?.targetSize}
                      onChange={() => dispatch(imageEditingActions.updateFeatureSettings({
                        featureName: 'compression',
                        settings: { ...features.compression.settings, targetSize: '5MB' }
                      }))}
                      className="sr-only"
                    />
                    <span className={`w-4 h-4 rounded-full border-2 mr-2 ${
                      features.compression.settings?.targetSize === '5MB' || !features.compression.settings?.targetSize
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-300'
                    }`}>
                      {(features.compression.settings?.targetSize === '5MB' || !features.compression.settings?.targetSize) && (
                        <span className="block w-2 h-2 bg-white rounded-full m-auto mt-0.5"></span>
                      )}
                    </span>
                    <span className="text-sm">5MB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="compressionSize"
                      value="1MB"
                      checked={features.compression.settings?.targetSize === '1MB'}
                      onChange={() => dispatch(imageEditingActions.updateFeatureSettings({
                        featureName: 'compression',
                        settings: { ...features.compression.settings, targetSize: '1MB' }
                      }))}
                      className="sr-only"
                    />
                    <span className={`w-4 h-4 rounded-full border-2 mr-2 ${
                      features.compression.settings?.targetSize === '1MB'
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-300'
                    }`}>
                      {features.compression.settings?.targetSize === '1MB' && (
                        <span className="block w-2 h-2 bg-white rounded-full m-auto mt-0.5"></span>
                      )}
                    </span>
                    <span className="text-sm">1MB</span>
                  </label>
                </div>
                    </div>
                  )}
                  {/* コントラスト調整の詳細設定 */}
                  {feature.id === 'contrastAdjustment' && features.contrastAdjustment?.enabled && (
                    <div className="-mt-2 p-2 bg-gray-50 rounded" style={{ marginLeft: '20px', marginRight: '12px' }}>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          value={features.contrastAdjustment.settings?.contrast || 0}
                          onChange={(e) => handleContrastChange(Number(e.target.value))}
                          className="flex-1"
                          style={{
                            background: `linear-gradient(to right, #cbd5e0 0%, #cbd5e0 ${50 + (features.contrastAdjustment.settings?.contrast || 0) / 2}%, #fb923c ${50 + (features.contrastAdjustment.settings?.contrast || 0) / 2}%, #fb923c 100%)`
                          }}
                        />
                        <div className="relative">
                          <input
                            type="number"
                            min="-100"
                            max="100"
                            step="1"
                            value={isContrastInputFocused ? contrastInputValue : (features.contrastAdjustment.settings?.contrast || 0)}
                            onChange={(e) => {
                              setContrastInputValue(e.target.value);
                            }}
                            onFocus={() => {
                              setIsContrastInputFocused(true);
                              setContrastInputValue(String(features.contrastAdjustment.settings?.contrast || 0));
                            }}
                            onBlur={() => {
                              setIsContrastInputFocused(false);
                              const value = contrastInputValue === '' ? 0 : Number(contrastInputValue);
                              const clampedValue = Math.max(-100, Math.min(100, value));
                              handleContrastChange(clampedValue);
                              setContrastInputValue('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                            }}
                            className="border border-gray-300 rounded px-1 py-0.5 pr-5 bg-white text-xs font-medium text-gray-700 text-center"
                            style={{ width: '50px' }}
                          />
                          <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 pointer-events-none">%</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 明るさ調整の詳細設定 */}
                  {feature.id === 'brightnessAdjustment' && features.brightnessAdjustment?.enabled && (
                    <div className="-mt-2 p-2 bg-gray-50 rounded" style={{ marginLeft: '20px', marginRight: '12px' }}>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          value={features.brightnessAdjustment.settings?.brightness || 0}
                          onChange={(e) => handleBrightnessChange(Number(e.target.value))}
                          className="flex-1"
                          style={{
                            background: `linear-gradient(to right, #cbd5e0 0%, #cbd5e0 ${50 + (features.brightnessAdjustment.settings?.brightness || 0) / 2}%, #fb923c ${50 + (features.brightnessAdjustment.settings?.brightness || 0) / 2}%, #fb923c 100%)`
                          }}
                        />
                        <div className="relative">
                          <input
                            type="number"
                            min="-100"
                            max="100"
                            step="1"
                            value={isBrightnessInputFocused ? brightnessInputValue : (features.brightnessAdjustment.settings?.brightness || 0)}
                            onChange={(e) => {
                              setBrightnessInputValue(e.target.value);
                            }}
                            onFocus={() => {
                              setIsBrightnessInputFocused(true);
                              setBrightnessInputValue(String(features.brightnessAdjustment.settings?.brightness || 0));
                            }}
                            onBlur={() => {
                              setIsBrightnessInputFocused(false);
                              const value = brightnessInputValue === '' ? 0 : Number(brightnessInputValue);
                              const clampedValue = Math.max(-100, Math.min(100, value));
                              handleBrightnessChange(clampedValue);
                              setBrightnessInputValue('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                            }}
                            className="border border-gray-300 rounded px-1 py-0.5 pr-5 bg-white text-xs font-medium text-gray-700 text-center"
                            style={{ width: '50px' }}
                          />
                          <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 pointer-events-none">%</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ホワイトバランス調整の詳細設定 */}
                  {feature.id === 'whiteBalance' && features.whiteBalance?.enabled && (
                    <div className="-mt-2 p-2 bg-gray-50 rounded" style={{ marginLeft: '20px', marginRight: '12px' }}>
                      <div className="flex gap-1 mb-1.5">
                        <button
                          type="button"
                          onClick={() => handleWhiteBalancePreset('sunlight')}
                          className={`flex-1 px-1.5 py-1 text-xs rounded transition-colors ${
                            features.whiteBalance.settings?.temperature === 4750
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          太陽光
                        </button>
                        <button
                          type="button"
                          onClick={() => handleWhiteBalancePreset('fluorescent')}
                          className={`flex-1 px-1.5 py-1 text-xs rounded transition-colors ${
                            features.whiteBalance.settings?.temperature === 5750
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          蛍光灯
                        </button>
                        <button
                          type="button"
                          onClick={() => handleWhiteBalancePreset('incandescent')}
                          className={`flex-1 px-1.5 py-1 text-xs rounded transition-colors ${
                            features.whiteBalance.settings?.temperature === 3750
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          電球色
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* プラン制限のある機能を囲むコンテナ */}
            {/* <div className="relative">
              <div className="space-y-2">
                {processingFeatures.slice(4).map((feature) => {
                  const featureState = features[feature.id as keyof typeof features];
                  const isEnabled = featureState?.enabled || false;
                  const isPlanRestricted = feature.planRestricted || false;

                  return (
                    <div
                      key={feature.id}
                      className="relative flex items-center justify-between p-2 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${isPlanRestricted ? 'text-gray-600' : 'text-gray-700'}`}>
                          {feature.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* モザイク加工〜ホワイトバランス調整のツールチップアイコンは非表示 */}
                    {/* <button
                      type="button"
                      onClick={() => !isPlanRestricted && handleFeatureToggle(feature.id)}
                      disabled={isPlanRestricted}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        isEnabled && !isPlanRestricted
                          ? 'bg-orange-500 text-white'
                          : isPlanRestricted
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                      }`}
                    >
                      ON
                    </button>
                    <button
                      type="button"
                      onClick={() => !isPlanRestricted && handleFeatureToggle(feature.id)}
                      disabled={isPlanRestricted}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        !isEnabled || isPlanRestricted
                          ? 'bg-gray-500 text-white'
                          : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                      }`}
                    >
                      OFF
                    </button>
                  </div>
                    </div>
                  );
                })}
              </div> */}
              {/* プラン制限オーバーレイ - モザイク加工からホワイトバランスまでを覆う */}
              {/* <div className="absolute inset-0 rounded-lg z-10" style={{ backgroundColor: 'rgba(209, 213, 219)', pointerEvents: 'none' }}> */}
                {/* Coming Soon - Basic/Pro/Eliteプランかつ個別処理の場合のみ表示 */}
                {/* {hasAdvancedFeatures && processingMethod === 'individual' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white text-xl font-bold underline">Coming Soon</span>
                  </div>
                )} */}
                {/* プラン制限メッセージ */}
                {/* {processingMethod !== 'individual' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-white text-base text-center" style={{ pointerEvents: 'auto' }}>
                      {(currentUser?.planType === 'Mini' || currentUser?.planType === 'Lite') ? (
                        <>
                          <strong>Basic/Pro/Eliteへ<br/>
                          アップグレードする必要があります</strong><br/>
                          <span className="text-sm">
                            Basic/Pro/Eliteプランの<br/>
                            「個別処理」でご利用可能です<br/>
                            ご希望の場合は<a href="https://docs.google.com/forms/d/e/1FAIpQLSeOnqLutSGFjjvo6Bo0Z9Ulq4vb6tcH8pGA0m39kxA9Z1xAVg/viewform" target="_blank" rel="noopener noreferrer" className="underline">こちらのフォーム</a><br/>
                            までお問合せください
                          </span>
                        </>
                      ) : (
                        <>
                          Basic/Pro/Eliteプランの<br/>
                          「個別処理」でご利用可能です<br/>
                        </>
                      )}
                    </div>
                  </div>
                )} */}
              {/* </div> */}
            {/* </div> */}
          </div>
        </div>
      </div>

      {/* クリア・保存ボタン */}
      <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
        {/* クリアボタン */}
        <button
          type="button"
          onClick={handleClearSettings}
          className={`w-1/2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            hasAnyEnabledSettings
              ? 'bg-gray-500 text-white hover:bg-gray-600'
              : 'text-gray-500 bg-gray-300 cursor-not-allowed'
          }`}
          disabled={!hasAnyEnabledSettings}
        >
          クリア
        </button>

        {/* 保存ボタン */}
        <button
          type="button"
          onClick={handleTemplateSave}
          className={`w-1/2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            hasAdvancedFeatures
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'text-gray-500 bg-gray-300 cursor-not-allowed'
          }`}
          disabled={!hasAdvancedFeatures}
        >
          設定を保存
        </button>
      </div>
      </div>

      {/* マイページモーダル */}
      {showMyPageModal && (
        <MyPageModal onClose={() => setShowMyPageModal(false)} initialTab="service" />
      )}

      {/* テンプレート保存モーダル */}
      <TemplateSaveModal
        show={showTemplateSaveModal}
        onHide={() => setShowTemplateSaveModal(false)}
      />

      {/* テンプレート上書きモーダル */}
      <TemplateOverwriteModal
        show={showTemplateOverwriteModal}
        onHide={() => setShowTemplateOverwriteModal(false)}
      />

      {/* クリア確認ポップアップ */}
      <HintPopup
        show={showClearHint}
        title="クリア"
        description={
          <div className="space-y-3">
            <p>現在の加工機能の設定をすべてクリアします。</p>
            <p className="text-orange-600 font-medium">
              この操作は他の画像にも影響します。
            </p>
            <p>設定をテンプレートとして保存するか、加工済み画像をダウンロードすることをお勧めします。</p>
          </div>
        }
        onClose={handleCloseClearHint}
        showCheckbox={true}
        checkboxValue={dontShowClearAgain}
        onCheckboxChange={setDontShowClearAgain}
        showExecuteButton={true}
        onExecute={handleExecuteClear}
        executeButtonText="クリア"
      />
    </div>
  );
};

export default connect(mapStateToProps)(ImageEditingSettingsPanel);